import { AlertCircle, CheckCircle, Clock, Trophy, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "urql";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
    type StalledGameIssue,
    TroubleshootingGameDocument,
    type TroubleshootingIssue,
    TroubleshootingRoundDocument,
    TroubleshootingTournamentDocument,
    type TroubleshootingTournamentQuery,
    TroubleshootingTrialDocument,
    type TroubleshootingTrialQuery,
} from "@/gen/graphql";

interface IssueDisplayWithDataProps {
    issue: TroubleshootingIssue | StalledGameIssue;
    type: "missing" | "orphaned" | "stalled";
}

type Player = NonNullable<TroubleshootingTrialQuery["trial"]>["player1"];

const getPlayerDisplayName = (player: Player | null | undefined): string => {
    if (!player) return "Unknown Player";
    return player.nickname || player.name || "Unknown Player";
};

const getTimeSince = (dateString: string | null | undefined): string | null => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffHours < 1) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        return `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""} ago`;
    } else if (diffHours < 24) {
        return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    } else {
        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
    }
};

const isOngoingTournament = (
    tournament: TroubleshootingTournamentQuery["tournament"] | null | undefined
): boolean => {
    if (!tournament) return false;
    const now = new Date();
    const startAt = new Date(tournament.startAt);
    const endAt = new Date(tournament.endAt);
    return now >= startAt && now <= endAt;
};

export function IssueDisplayWithData({ issue, type }: IssueDisplayWithDataProps) {
    const [tournamentResult] = useQuery({
        query: TroubleshootingTournamentDocument,
        variables: { id: issue.tournamentId },
    });

    const [trialResult] = useQuery({
        query: TroubleshootingTrialDocument,
        variables: { id: issue.trialId },
    });

    const [roundResult] = useQuery({
        query: TroubleshootingRoundDocument,
        variables: { id: issue.roundId },
    });

    const [gameResult] = useQuery({
        query: TroubleshootingGameDocument,
        variables: { id: issue.gameId! },
        pause: !issue.gameId,
    });

    const tournament = tournamentResult.data?.tournament;
    const trial = trialResult.data?.trial;
    const round = roundResult.data?.round;
    const game = gameResult.data?.game;

    const isLoading =
        tournamentResult.fetching ||
        trialResult.fetching ||
        roundResult.fetching ||
        gameResult.fetching;

    if (isLoading) {
        return (
            <Card>
                <CardHeader>
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="mt-2 h-3 w-32" />
                </CardHeader>
                <CardContent className="space-y-2">
                    <Skeleton className="h-4 w-64" />
                    <Skeleton className="h-3 w-40" />
                </CardContent>
            </Card>
        );
    }

    const isOngoing = isOngoingTournament(tournament);
    const tournamentName = tournament?.shortTitle || tournament?.title || "Unknown Tournament";
    const player1Name = getPlayerDisplayName(trial?.player1);
    const player2Name = getPlayerDisplayName(trial?.player2);

    // Use the gameCount from the issue data if available (for missing trial results from backend)
    // Otherwise fall back to counting games from the trial query (for orphaned game results)
    const gameCount = "gameCount" in issue ? issue.gameCount : (trial?.games?.length ?? 0);

    // For missing trial results:
    // - Single-game trials are ALWAYS auto-fixable
    // - Multi-game trials are usually fixable but depend on format calculation
    // For missing game results:
    // - Only single-game trials are auto-fixable
    // - Multi-game trials always need manual intervention
    const isAutofixable = gameCount === 1;
    const isLikelyFixable = type === "missing" && gameCount > 1;

    const relevantDate =
        game?.finishedAt || game?.startedAt || game?.scheduledStartAt || round?.scheduledStartAt;
    const timeSince = getTimeSince(relevantDate);

    return (
        <Card className="transition-shadow hover:shadow-md">
            <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                    <div className="space-y-1">
                        <div className="flex items-center gap-2">
                            <Trophy className="text-muted-foreground h-4 w-4" />
                            <CardTitle className="text-base font-semibold">
                                {tournamentName}
                            </CardTitle>
                            {isOngoing && (
                                <Badge variant="default" className="text-xs">
                                    Ongoing
                                </Badge>
                            )}
                        </div>
                        {round && (
                            <CardDescription className="text-sm">{round.title}</CardDescription>
                        )}
                    </div>
                    {timeSince && (
                        <div className="text-muted-foreground flex items-center gap-1 text-xs">
                            <Clock className="h-3 w-3" />
                            {timeSince}
                        </div>
                    )}
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                    <Users className="text-muted-foreground h-4 w-4" />
                    <span className="text-sm font-medium">
                        {player1Name} vs {player2Name}
                    </span>
                    <Button asChild variant="outline" size="sm" className="h-6 px-2 text-xs">
                        <Link
                            to={`/tournaments/${issue.tournamentId}/stages/${issue.tournamentStageId}/rounds/${issue.roundId}/trials/${issue.trialId}`}
                            target="_blank"
                        >
                            Trial
                        </Link>
                    </Button>
                    {issue.gameId && (
                        <Button asChild variant="outline" size="sm" className="h-6 px-2 text-xs">
                            <Link
                                to={`/tournaments/${issue.tournamentId}/stages/${issue.tournamentStageId}/rounds/${issue.roundId}/trials/${issue.trialId}/games/${issue.gameId}`}
                                target="_blank"
                            >
                                Game
                            </Link>
                        </Button>
                    )}
                </div>

                <div className="flex items-center gap-2 text-xs">
                    <Badge variant="destructive" className="text-xs">
                        {type === "missing"
                            ? "Missing trial result"
                            : type === "orphaned"
                              ? "Missing game result"
                              : "Stalled game"}
                    </Badge>
                    {type === "stalled" ? (
                        "reason" in issue &&
                        issue.reason && (
                            <Badge variant="outline" className="text-xs">
                                <AlertCircle className="mr-1 h-3 w-3" />
                                {issue.reason}
                            </Badge>
                        )
                    ) : (
                        <>
                            {isAutofixable ? (
                                <Badge variant="secondary" className="text-xs">
                                    <CheckCircle className="mr-1 h-3 w-3" />
                                    Auto-fixable
                                </Badge>
                            ) : isLikelyFixable ? (
                                <Badge variant="secondary" className="text-xs">
                                    <CheckCircle className="mr-1 h-3 w-3" />
                                    Likely fixable ({gameCount} games)
                                </Badge>
                            ) : (
                                <Badge variant="outline" className="text-xs">
                                    <AlertCircle className="mr-1 h-3 w-3" />
                                    {gameCount} games
                                </Badge>
                            )}
                        </>
                    )}
                    {trial?.result && (
                        <span className="text-muted-foreground">Trial result: {trial.result}</span>
                    )}
                    {game?.result && (
                        <span className="text-muted-foreground">Game result: {game.result}</span>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
